/*
 * Login Component
 * 
 * Provides user login interface with options to:
 * - Switch to registration
 * - Access admin panel
 * - Email/password authentication
 * - PWA installation functionality
 * 
 * TO EDIT LOGIN FORM:
 * - Add/remove form fields in the form section
 * - Modify validation logic in handleSubmit
 * - Update styling by changing Tailwind classes
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import SocialLoginButtons from './SocialLoginButtons';
import logoImage from '../../assets/images/bmpCITCO.png';
import { APP_BRANDING } from '../../utils/config';

// onSwitchToAdmin,
const Login = ({ onSwitchToRegister,  onSwitchToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true for persistent sessions
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { canInstall, installApp, showInstallInstructions, isIOS } = usePWAInstall();

  /**
   * Handle form submission and user authentication
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Attempt to login user with remember me option
    const result = await login(email, password, rememberMe);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  /**
   * Handle PWA installation
   */
  const handleInstallClick = async () => {
    await installApp();
    // installApp() handles showing instructions internally for iOS and fallback cases
  };

  const handleSocialLoginSuccess = (user, isNewUser) => {
    // The social login component handles token storage and axios headers
    // The AuthContext will detect the token and update the user state
    window.location.reload(); // Refresh to trigger auth state update
  };

  const handleSocialLoginError = (error) => {
    setError(error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 w-full max-w-md">
        {/* PWA Install Button */}
        {canInstall && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-blue-600 text-xl mr-2">📱</span>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    {isIOS ? 'Add to Home Screen' : 'Install App'}
                  </p>
                  <p className="text-xs text-blue-600">
                    {isIOS
                      ? 'Get quick access from your home screen'
                      : 'Add to home screen for better experience'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleInstallClick}
                className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 whitespace-nowrap"
              >
                {isIOS ? 'How to Add' : 'Install'}
              </button>
            </div>
          </div>
        )}
        
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src={logoImage}
              alt={`${APP_BRANDING.shortName} Logo`}
              className="h-12 w-12 sm:h-16 sm:w-16 mr-3 object-contain"
            />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">{APP_BRANDING.shortName}</h2>
              <p className="text-gray-600 text-sm sm:text-base mt-1">{APP_BRANDING.description}</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
            />
            <div className="text-right mt-2">
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {/* Remember Me Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="remember-me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Keep me signed in (recommended)
            </label>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-medium"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Social Login Buttons */}
        <SocialLoginButtons 
          onLoginSuccess={handleSocialLoginSuccess}
          onLoginError={handleSocialLoginError}
        />

        {/* Facebook Promotion */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-1">Stay Connected with Belmopan City Council!</h3>
              <p className="text-xs text-gray-600 mb-2">
                Follow us on Facebook for the latest updates, feature announcements, and community news.
              </p>
              <a
                href="https://www.facebook.com/bmp.citycouncil"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Follow on Facebook
              </a>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {/* Registration Link */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Sign up
              </button>
            </p>
          </div>

          {/* Driver Login Link */}
          <div className="text-center pt-3 border-t border-gray-200">
            <a
              href="/driver"
              className="text-sm text-green-700 hover:text-green-900 font-medium"
            >
              🚛 Garbage truck driver? Log in here
            </a>
          </div>

          {/* Admin Panel Link */}
          {/* <div className="text-center pt-3 border-t border-gray-200">
            <button
              onClick={onSwitchToAdmin}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
            >
              🔐 Admin Panel Access
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Login;